import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  TablePagination,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import myAxios from '../AxiosUrl';
import './RedisManagement.css';

const RedisManagement = () => {
  const [stats, setStats] = useState(null);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  // Form states
  const [searchPattern, setSearchPattern] = useState('*');
  const [searchLimit, setSearchLimit] = useState(100);
  const [invalidateAction, setInvalidateAction] = useState('profile');
  const [invalidateUserId, setInvalidateUserId] = useState('');
  const [invalidatePattern, setInvalidatePattern] = useState('');
  const [invalidateKey, setInvalidateKey] = useState('');

  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null });
  const [viewKeysDialog, setViewKeysDialog] = useState({ open: false, pattern: '', keys: [], loading: false, page: 0, rowsPerPage: 25 });
  const [jsonViewDialog, setJsonViewDialog] = useState({ open: false, value: '', keyName: '' });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await myAxios.get('/redis-stats');
      if (response.data.success) {
        setStats(response.data.stats);
      } else {
        setMessage({ text: response.data.error, type: 'error' });
      }
    } catch (error) {
      setMessage({ text: error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const response = await myAxios.post('/redis-keys', {
        pattern: searchPattern,
        limit: searchLimit,
      });
      if (response.data.success) {
        setKeys(response.data.keys);
        setMessage({
          text: `Found ${response.data.total} keys, showing ${response.data.showing}`,
          type: 'info',
        });
      } else {
        setMessage({ text: response.data.error, type: 'error' });
      }
    } catch (error) {
      setMessage({ text: error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const invalidateCache = async () => {
    setLoading(true);
    try {
      const payload = { action: invalidateAction };

      if (invalidateAction === 'profile' || invalidateAction === 'subscription') {
        if (!invalidateUserId) {
          setMessage({ text: 'User ID is required', type: 'error' });
          setLoading(false);
          return;
        }
        payload.userId = invalidateUserId;
      } else if (invalidateAction === 'pattern') {
        if (!invalidatePattern) {
          setMessage({ text: 'Pattern is required', type: 'error' });
          setLoading(false);
          return;
        }
        payload.pattern = invalidatePattern;
      } else if (invalidateAction === 'key') {
        if (!invalidateKey) {
          setMessage({ text: 'Key is required', type: 'error' });
          setLoading(false);
          return;
        }
        payload.key = invalidateKey;
      }

      const response = await myAxios.post('/redis-invalidate', payload);

      if (response.data.success) {
        setMessage({
          text: `✅ ${response.data.message} (${response.data.keysDeleted} keys deleted)`,
          type: 'success',
        });

        // Refresh stats and keys
        fetchStats();
        if (keys.length > 0) {
          fetchKeys();
        }

        // Clear inputs
        setInvalidateUserId('');
        setInvalidatePattern('');
        setInvalidateKey('');
      } else {
        setMessage({ text: response.data.error, type: 'error' });
      }
    } catch (error) {
      setMessage({ text: error.message, type: 'error' });
    } finally {
      setLoading(false);
      setConfirmDialog({ open: false, action: null });
    }
  };

  const handleInvalidateClick = () => {
    setConfirmDialog({
      open: true,
      action: invalidateAction,
    });
  };

  const handleViewKeys = async (pattern) => {
    setViewKeysDialog({ open: true, pattern, keys: [], loading: true, error: null, page: 0, rowsPerPage: 25 });

    try {
      const response = await myAxios.post('/redis-keys', {
        pattern: pattern,
        limit: 100, // Load up to 100 keys (reduced for performance)
      }, {
        timeout: 30000, // 30 second timeout
      });

      if (response.data.success) {
        setViewKeysDialog({
          open: true,
          pattern,
          keys: response.data.keys,
          loading: false,
          total: response.data.total,
          showing: response.data.showing,
          error: null,
          page: 0,
          rowsPerPage: 25,
        });
      } else {
        const errorMsg = response.data.error || 'Failed to load keys';
        setMessage({ text: errorMsg, type: 'error' });
        setViewKeysDialog({
          open: true,
          pattern,
          keys: [],
          loading: false,
          error: errorMsg,
          page: 0,
          rowsPerPage: 25,
        });
      }
    } catch (error) {
      console.error('Error loading keys:', error);

      // Handle different error types
      let errorMsg = 'Failed to load keys';
      if (error.code === 'ECONNABORTED' || error.message === 'Request aborted') {
        errorMsg = 'Request was cancelled. Please try again and wait for the response.';
      } else if (error.code === 'ECONNREFUSED') {
        errorMsg = 'Cannot connect to backend server. Make sure it is running.';
      } else if (error.response?.status === 401) {
        errorMsg = 'Unauthorized. Please login as an admin user.';
      } else {
        errorMsg = error.response?.data?.msg || error.response?.data?.error || error.message || errorMsg;
      }

      setMessage({ text: errorMsg, type: 'error' });
      setViewKeysDialog({
        open: true,
        pattern,
        keys: [],
        loading: false,
        error: errorMsg,
        page: 0,
        rowsPerPage: 25,
      });
    }
  };

  const handleChangePage = (event, newPage) => {
    setViewKeysDialog(prev => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setViewKeysDialog(prev => ({ ...prev, rowsPerPage: parseInt(event.target.value, 10), page: 0 }));
  };

  const handleViewJson = async (keyData) => {
    // If preview says "Click 'View JSON' to load value", fetch from server
    if (keyData.preview === "Click 'View JSON' to load value" || !keyData.preview) {
      setJsonViewDialog({ open: true, value: 'Loading...', keyName: keyData.key, loading: true });

      try {
        const response = await myAxios.post('/redis-key-value', {
          key: keyData.key
        });

        if (response.data.success) {
          let formattedValue = '';
          try {
            const parsed = JSON.parse(response.data.value);
            formattedValue = JSON.stringify(parsed, null, 2);
          } catch {
            formattedValue = response.data.value;
          }
          setJsonViewDialog({ open: true, value: formattedValue, keyName: keyData.key, loading: false });
        } else {
          setJsonViewDialog({ open: true, value: `Error: ${response.data.error}`, keyName: keyData.key, loading: false });
        }
      } catch (error) {
        setJsonViewDialog({ open: true, value: `Error: ${error.message}`, keyName: keyData.key, loading: false });
      }
    } else {
      // Use cached preview
      let formattedValue = '';
      try {
        const parsed = JSON.parse(keyData.preview);
        formattedValue = JSON.stringify(parsed, null, 2);
      } catch {
        formattedValue = keyData.preview || 'No value available';
      }
      setJsonViewDialog({ open: true, value: formattedValue, keyName: keyData.key, loading: false });
    }
  };

  const getKeyTypeColor = (type) => {
    switch (type) {
      case 'string':
        return 'primary';
      case 'set':
        return 'secondary';
      case 'hash':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Redis Cache Management
      </Typography>

      {message.text && (
        <Alert
          severity={message.type}
          onClose={() => setMessage({ text: '', type: '' })}
          sx={{ mb: 3 }}
        >
          {message.text}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Keys
              </Typography>
              <Typography variant="h4">
                {stats ? stats.totalKeys : '-'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Cache Hit Rate
              </Typography>
              <Typography variant="h4" color="primary">
                {stats ? stats.hitRate : '-'}
              </Typography>
              <Typography variant="caption">
                {stats ? `${stats.hits} hits / ${stats.misses} misses` : ''}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Memory Used
              </Typography>
              <Typography variant="h4">
                {stats ? stats.usedMemory : '-'}
              </Typography>
              <Typography variant="caption" display="block">
                Peak: {stats ? stats.usedMemoryPeak : '-'}
              </Typography>
              <Typography variant="caption" color="primary">
                {stats ? stats.memoryUsagePercent : '-'} of total
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                System Memory
              </Typography>
              <Typography variant="h4">
                {stats ? stats.totalSystemMemory : '-'}
              </Typography>
              <Typography variant="caption" display="block">
                Clients: {stats ? stats.connectedClients : '-'}
              </Typography>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={fetchStats}
                sx={{ mt: 1 }}
              >
                Refresh
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* All Redis Keys by Category */}
      {stats && stats.keyDetails && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              All Redis Keys Used in Project
            </Typography>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                    <TableCell><strong>Category</strong></TableCell>
                    <TableCell><strong>Pattern</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell align="right"><strong>Count</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.keyDetails.map((keyInfo, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={keyInfo.category}
                          size="small"
                          color={
                            keyInfo.category === 'Dashboard' ? 'primary' :
                            keyInfo.category === 'Profile' ? 'secondary' :
                            keyInfo.category === 'Web' ? 'info' :
                            'warning'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {keyInfo.pattern}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {keyInfo.description}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={keyInfo.count.toLocaleString()}
                          size="small"
                          color={keyInfo.count > 100 ? 'error' : keyInfo.count > 10 ? 'warning' : keyInfo.count > 0 ? 'success' : 'default'}
                          variant={keyInfo.count === 0 ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {keyInfo.count > 0 && (
                            <Tooltip title={`View all ${keyInfo.pattern} keys`}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleViewKeys(keyInfo.pattern)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {keyInfo.canInvalidate && keyInfo.count > 0 && (
                            <Tooltip title={`Clear all ${keyInfo.pattern} keys`}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setInvalidatePattern(keyInfo.pattern);
                                  setInvalidateAction('pattern');
                                  setConfirmDialog({
                                    open: true,
                                    action: 'pattern',
                                    pattern: keyInfo.pattern,
                                    count: keyInfo.count
                                  });
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Total Patterns:</strong> {stats.keyDetails.length} |
                <strong> Active Patterns:</strong> {stats.keyDetails.filter(k => k.count > 0).length} |
                <strong> Total Keys:</strong> {stats.keyDetails.reduce((sum, k) => sum + k.count, 0).toLocaleString()}
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Search Keys */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Search Redis Keys
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Key Pattern"
                value={searchPattern}
                onChange={(e) => setSearchPattern(e.target.value)}
                placeholder="e.g., viewcv:*, filter:ids:*, subscription:details:*"
                helperText="Use * as wildcard"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Limit"
                value={searchLimit}
                onChange={(e) => setSearchLimit(parseInt(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={fetchKeys}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              >
                Search
              </Button>
            </Grid>
          </Grid>

          {/* Keys Table */}
          {keys.length > 0 && (
            <TableContainer component={Paper} sx={{ mt: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Key</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>TTL</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Preview</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {keys.map((keyData, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {keyData.key}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={keyData.type} size="small" color={getKeyTypeColor(keyData.type)} />
                      </TableCell>
                      <TableCell>{keyData.ttl}</TableCell>
                      <TableCell>{keyData.size}</TableCell>
                      <TableCell>
                        <Tooltip title={keyData.preview || 'No preview'}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                            {keyData.preview || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Invalidate Cache */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Invalidate Cache
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Action</InputLabel>
                <Select
                  value={invalidateAction}
                  onChange={(e) => setInvalidateAction(e.target.value)}
                  label="Action"
                >
                  <MenuItem value="profile">User Profile</MenuItem>
                  <MenuItem value="subscription">User Subscription</MenuItem>
                  <MenuItem value="filter">All Filters</MenuItem>
                  <MenuItem value="pattern">By Pattern</MenuItem>
                  <MenuItem value="key">Specific Key</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {(invalidateAction === 'profile' || invalidateAction === 'subscription') && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="User ID"
                  value={invalidateUserId}
                  onChange={(e) => setInvalidateUserId(e.target.value)}
                  placeholder="Enter user ID"
                />
              </Grid>
            )}

            {invalidateAction === 'pattern' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Pattern"
                  value={invalidatePattern}
                  onChange={(e) => setInvalidatePattern(e.target.value)}
                  placeholder="e.g., viewcv:*, filter:ids:*, search:*"
                />
              </Grid>
            )}

            {invalidateAction === 'key' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Key"
                  value={invalidateKey}
                  onChange={(e) => setInvalidateKey(e.target.value)}
                  placeholder="Enter exact key"
                />
              </Grid>
            )}

            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                color="error"
                onClick={handleInvalidateClick}
                disabled={loading}
                startIcon={<DeleteIcon />}
              >
                Invalidate
              </Button>
            </Grid>
          </Grid>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              ⚠️ <strong>Warning:</strong> Cache invalidation will delete keys immediately.
              Use "All Filters" action with caution as it affects all users.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: null })}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            Confirm Cache Invalidation
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to invalidate cache using action: <strong>{confirmDialog.action}</strong>?
          </Typography>
          {confirmDialog.pattern && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Pattern: <code>{confirmDialog.pattern}</code>
                <br />
                Keys to delete: <strong>{confirmDialog.count}</strong>
              </Typography>
            </Alert>
          )}
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            ⚠️ This action cannot be undone. Deleted cache will be regenerated on next request.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: null })}>
            Cancel
          </Button>
          <Button onClick={invalidateCache} color="error" variant="contained" startIcon={<DeleteIcon />}>
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Keys Dialog */}
      <Dialog
        open={viewKeysDialog.open}
        onClose={() => setViewKeysDialog({ open: false, pattern: '', keys: [], loading: false })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon color="primary" />
              <Typography variant="h6">
                View Keys: <code style={{ fontSize: '0.9em', color: '#1976d2' }}>{viewKeysDialog.pattern}</code>
              </Typography>
            </Box>
            <IconButton
              onClick={() => setViewKeysDialog({ open: false, pattern: '', keys: [], loading: false })}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {viewKeysDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading keys...</Typography>
            </Box>
          ) : viewKeysDialog.error ? (
            <Alert severity="error">
              <Typography variant="body2">
                <strong>Error:</strong> {viewKeysDialog.error}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Please make sure you're logged into the admin panel and have a valid token.
              </Typography>
            </Alert>
          ) : viewKeysDialog.keys.length > 0 ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Showing <strong>{viewKeysDialog.showing}</strong> of <strong>{viewKeysDialog.total}</strong> keys
                  {viewKeysDialog.total > viewKeysDialog.showing && (
                    <> (limited to first 100 keys for performance)</>
                  )}
                </Typography>
              </Alert>
              <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Key</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>TTL</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Size</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Value Preview</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {viewKeysDialog.keys
                      .slice(viewKeysDialog.page * viewKeysDialog.rowsPerPage, viewKeysDialog.page * viewKeysDialog.rowsPerPage + viewKeysDialog.rowsPerPage)
                      .map((keyData, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            {keyData.key}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={keyData.type}
                            size="small"
                            color={getKeyTypeColor(keyData.type)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            {keyData.ttl}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            {keyData.size}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={keyData.preview || 'No preview available'} arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.85rem',
                                maxWidth: 400,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                cursor: 'help'
                              }}
                            >
                              {keyData.preview || '-'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View full JSON">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewJson(keyData)}
                            >
                              <CodeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={viewKeysDialog.keys.length}
                page={viewKeysDialog.page}
                onPageChange={handleChangePage}
                rowsPerPage={viewKeysDialog.rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          ) : (
            <Alert severity="info">No keys found for this pattern.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setViewKeysDialog({ open: false, pattern: '', keys: [], loading: false })}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* JSON View Dialog */}
      <Dialog
        open={jsonViewDialog.open}
        onClose={() => setJsonViewDialog({ open: false, value: '', keyName: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CodeIcon color="primary" />
              <Typography variant="h6">JSON View</Typography>
            </Box>
            <IconButton
              onClick={() => setJsonViewDialog({ open: false, value: '', keyName: '' })}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
            Key: <code>{jsonViewDialog.keyName}</code>
          </Typography>
          <Paper
            sx={{
              p: 2,
              backgroundColor: '#f5f5f5',
              maxHeight: '60vh',
              overflow: 'auto',
              border: '1px solid #e0e0e0',
            }}
          >
            <pre
              style={{
                margin: 0,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {jsonViewDialog.value}
            </pre>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              navigator.clipboard.writeText(jsonViewDialog.value);
              setMessage({ text: 'Copied to clipboard!', type: 'success' });
            }}
            startIcon={<CheckCircleIcon />}
          >
            Copy to Clipboard
          </Button>
          <Button
            onClick={() => setJsonViewDialog({ open: false, value: '', keyName: '' })}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RedisManagement;
